<template>
  <section id="home" class="hero">
    <div class="hero-bg"></div>
    <div class="container hero-content">
      <div class="hero-left">
        <div class="hero-icon">
          <IconSvg name="target" class="target-icon" />
        </div>
        <h1>
          {{ t('hero.title') }}
          <span class="badge">{{ t('hero.badge') }}</span>
        </h1>
        <p class="subtitle">{{ t('hero.subtitle') }}</p>
        <p class="desc">{{ t('hero.desc') }}</p>
        <div class="tags">
          <span v-for="tag in tags" :key="tag" class="tag">{{ tag }}</span>
        </div>
        <a href="#download" class="cta" @click.prevent="scrollTo('download')">
          {{ t('hero.cta') }}
          <IconSvg name="sword" class="cta-icon" />
        </a>
      </div>
      <div class="hero-right">
        <div class="shield-card">
          <IconSvg name="shield" class="shield-icon" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import IconSvg from './IconSvg.vue'

const { t, tm } = useI18n()
const tags = computed(() => tm('hero.tags'))

const scrollTo = (id) => {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth' })
}
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  color: #fff;
  overflow: hidden;
  padding-top: 80px;
}
.hero-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 70% 30%, rgba(239, 68, 68, 0.25), transparent 50%),
              linear-gradient(135deg, #1a0505 0%, #4a0a0a 50%, #2a0a0a 100%);
  z-index: -2;
}
.hero-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: 0.4;
}
.hero-content {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 3rem;
  align-items: center;
  width: 100%;
}
.hero-left {
  max-width: 600px;
}
.hero-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(255, 107, 53, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: var(--accent);
}
.target-icon {
  width: 36px;
  height: 36px;
}
h1 {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 900;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.badge {
  font-size: 0.75rem;
  background: var(--accent);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-weight: 700;
  letter-spacing: 0.05em;
}
.subtitle {
  font-size: 1.2rem;
  color: rgba(255,255,255,0.75);
  margin: 0 0 1.5rem;
  font-weight: 500;
}
.desc {
  font-size: 1.05rem;
  line-height: 1.8;
  color: rgba(255,255,255,0.85);
  margin: 0 0 1.5rem;
}
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 2rem;
}
.tag {
  padding: 0.4rem 1rem;
  border-radius: 999px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.15);
  font-size: 0.85rem;
  color: rgba(255,255,255,0.9);
}
.cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--accent);
  color: #fff;
  padding: 0.9rem 1.75rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.05rem;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 8px 24px rgba(255, 107, 53, 0.35);
}
.cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(255, 107, 53, 0.45);
}
.cta-icon {
  width: 20px;
  height: 20px;
}
.hero-right {
  display: flex;
  justify-content: center;
  align-items: center;
}
.shield-card {
  width: 320px;
  height: 320px;
  border-radius: 32px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05));
  border: 1px solid rgba(96, 165, 250, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 24px 80px rgba(0,0,0,0.35), inset 0 0 60px rgba(96, 165, 250, 0.1);
  animation: float 6s ease-in-out infinite;
}
.shield-icon {
  width: 140px;
  height: 140px;
  color: #60a5fa;
}
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
}
@media (max-width: 900px) {
  .hero-content {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .hero-left {
    max-width: 100%;
  }
  .hero-icon {
    margin-left: auto;
    margin-right: auto;
  }
  h1 {
    justify-content: center;
  }
  .tags {
    justify-content: center;
  }
  .cta {
    margin: 0 auto;
  }
  .shield-card {
    width: 260px;
    height: 260px;
  }
  .shield-icon {
    width: 100px;
    height: 100px;
  }
}
</style>
